import type {
  Acceptance_Status_Enum,
  InsertAcceptanceMutation,
  InsertAcceptanceMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertAcceptanceDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type InsertAcceptanceTRPCInput = Omit<
  InsertAcceptanceMutationVariables,
  'Title' | 'Details' | 'Status'
> & {
  Title: string;
  Details: string;
  Status: Acceptance_Status_Enum;
};

export const useInsertAcceptance = createMutationHook({
  name: 'insertAcceptance',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.acceptance.insert.mutationOptions({ onSuccess }),
  mapInput: (
    variables: InsertAcceptanceMutationVariables
  ): InsertAcceptanceTRPCInput => {
    const { Title, Details, Status, ...rest } = variables;
    if (!Title) {
      throw new Error('Title is required for acceptance insert');
    }
    if (!Status) {
      throw new Error('Status is required for acceptance insert');
    }

    return {
      ...rest,
      Title,
      // Details is optional (unrequirable field); send '' when empty to match
      // the legacy path and the V3 update schema (COI-482).
      Details: Details ?? '',
      Status,
    };
  },
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed `acceptanceCount`
    // (the left-nav badge) and `getByParentRiskId` (the risk's Acceptances tab).
    trpc.frontend.acceptance.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertAcceptanceMutation => ({
    insertChildAcceptance: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertAcceptanceDocument,
  graphqlVariables: (variables: InsertAcceptanceMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['acceptance', 'acceptance_aggregate', 'change_request'],
  refetchQueries: [namedOperations.Query.getAcceptanceById],
});
